/**
 * A small frontmatter parser for help articles. It understands the subset of
 * YAML the articles use: `key: value`, quoted strings, integers, inline lists
 * (`[a, b]`), and block lists (`- a`). No Node-only dependencies.
 */

export type FrontmatterValue = string | number | boolean | string[]

export interface ParsedFile {
  data: Record<string, FrontmatterValue>
  body: string
  /** Problems found while parsing, for the validation test. */
  errors: string[]
}

const FENCE = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/

function unquote(s: string): string {
  const t = s.trim()
  if (t.length >= 2 && ((t[0] === '"' && t.endsWith('"')) || (t[0] === "'" && t.endsWith("'")))) {
    const inner = t.slice(1, -1)
    return t[0] === '"'
      ? inner.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
      : inner.replace(/''/g, "'")
  }
  return t
}

/** Strip a trailing `# comment` that is not inside quotes. */
function stripComment(s: string): string {
  let quote: string | null = null
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (quote) {
      if (c === quote) quote = null
    } else if (c === '"' || c === "'") {
      quote = c
    } else if (c === '#' && (i === 0 || /\s/.test(s[i - 1]!))) {
      return s.slice(0, i).trimEnd()
    }
  }
  return s
}

function splitList(inner: string): string[] {
  const out: string[] = []
  let cur = ''
  let quote: string | null = null
  for (const c of inner) {
    if (quote) {
      cur += c
      if (c === quote) quote = null
    } else if (c === '"' || c === "'") {
      quote = c
      cur += c
    } else if (c === ',') {
      out.push(cur)
      cur = ''
    } else cur += c
  }
  out.push(cur)
  return out.map(unquote).filter((v) => v !== '')
}

function scalar(raw: string): FrontmatterValue {
  const v = raw.trim()
  if (v.startsWith('[') && v.endsWith(']')) return splitList(v.slice(1, -1))
  if (/^-?\d+$/.test(v)) return Number(v)
  if (v === 'true' || v === 'false') return v === 'true'
  return unquote(v)
}

export function parseFrontmatter(source: string): ParsedFile {
  const text = source.replace(/^\uFEFF/, '')
  const m = FENCE.exec(text)
  if (!m) return { data: {}, body: text, errors: ['missing frontmatter block'] }
  const data: Record<string, FrontmatterValue> = {}
  const errors: string[] = []
  let listKey: string | null = null
  // Join flow lists that wrap across lines (`related:\n  [\n    a,\n    b,\n  ]`).
  const lines: string[] = []
  for (const raw of m[1]!.split(/\r?\n/)) {
    const prev = lines[lines.length - 1]
    const open = prev !== undefined && /\[[^\]]*$/.test(stripComment(prev))
    if (open || (prev !== undefined && /:\s*$/.test(prev) && /^\s+\[/.test(raw)))
      lines[lines.length - 1] = `${prev} ${raw.trim()}`
    else lines.push(raw)
  }
  for (const rawLine of lines) {
    const line = stripComment(rawLine)
    if (line.trim() === '') continue
    const item = /^\s+-\s+(.*)$/.exec(line) ?? /^-\s+(.*)$/.exec(line)
    if (item && listKey) {
      ;(data[listKey] as string[]).push(unquote(item[1]!))
      continue
    }
    const kv = /^([A-Za-z_][\w-]*)\s*:\s*(.*)$/.exec(line)
    if (!kv) {
      errors.push(`cannot parse frontmatter line: ${rawLine}`)
      continue
    }
    const key = kv[1]!
    const value = kv[2]!
    if (key in data) errors.push(`duplicate frontmatter key: ${key}`)
    if (value.trim() === '') {
      data[key] = []
      listKey = key
    } else {
      data[key] = scalar(value)
      listKey = null
    }
  }
  return { data, body: text.slice(m[0].length), errors }
}
